import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";

const referenceAttr = "data-reference";
const removeAllSelector = ".ux-filter__remove-all";
@Component({
    tag: "ux-filter",
    styleUrl: "filter.scss",
    shadow: false
})
export class Filter {
    /**
     * This is a URL to a JSON API.
     */
    @Prop({ reflect: true }) readonly endpoint: string;

    /**
     * State to hold filter values.
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    @State() filterValues: Array<any>;

    @Element() domNode!: HTMLUxFilterElement;

    /** the HTML Element containing the tag list */
    private tagList: HTMLElement | null = null;

    /**
     * Listen for uxRemove event.
     */
    @Listen("uxRemove")
    tagRemovedHandler(event: CustomEvent) {
        const target = event.target as HTMLElement | null;
        const targetReference = target?.getAttribute("reference");
        if (!target || !targetReference) {
            return;
        }
        const checkbox = this.domNode.querySelector(
            `input[type="checkbox"][${referenceAttr}="${targetReference}"]`
        ) as HTMLInputElement;
        if (checkbox !== null) {
            this.uncheck(checkbox);
            this.filterChange.emit();
            this.removeTag(target);
            this.updateRemoveAll();
        }
    }

    /**
     * Is triggered when any input inside the filter was changed.
     */
    @Event({
        eventName: "uxFilterChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    filterChange!: EventEmitter;

    private onSubmit = (e: Event): void => {
        e.preventDefault();
    };

    private onChange = (e: Event): void => {
        const checkbox = e.target as HTMLElement;
        if (e instanceof CustomEvent && e.detail?.isFilterUncheck) {
            return;
        }
        if (checkbox.matches('input[type="checkbox"]')) {
            if (checkbox.matches(`:not(:checked)[${referenceAttr}]`)) {
                const reference = checkbox.getAttribute(referenceAttr);
                const tags = this.domNode.querySelectorAll(`ux-tag[reference="${reference}"]`);
                [].forEach.call(tags, (tag: HTMLElement) => tag.remove());
            } else {
                this.addTag(checkbox as HTMLInputElement);
            }
        }
        this.filterChange.emit(e);
        this.updateRemoveAll();
    };

    private uncheck(checkbox: HTMLInputElement): void {
        checkbox.checked = false;
        checkbox.dispatchEvent(new CustomEvent("change", { detail: { isFilterUncheck: true } }));
    }

    private addTag(checkbox: HTMLInputElement): void {
        const reference = getUniqueId();
        checkbox.setAttribute(referenceAttr, reference);
        if (this.tagList === null) {
            return;
        }
        // @ts-expect-error Check for typing
        const labelText = checkbox.closest("label").innerText.replace(/^[\s]+/, "");
        const tag = document.createElement("ux-tag");
        tag.setAttribute("reference", reference);
        tag.setAttribute("removable", "true");
        tag.innerText = labelText;
        this.tagList.appendChild(tag);
    }

    private removeTag(el: HTMLElement | null): void {
        if (!el) {
            return;
        }
        el.remove();
    }

    private updateRemoveAll(): void {
        const btnRemoveAll = this.domNode.querySelector(removeAllSelector);
        if (btnRemoveAll === null) {
            return;
        }
        setTimeout(() => {
            const activeFilters = this.domNode.querySelectorAll(`input[type="checkbox"][${referenceAttr}]:checked`);
            if (activeFilters.length > 0) {
                btnRemoveAll.classList.remove("is-hidden");
            } else if (!btnRemoveAll.classList.contains("is-hidden")) {
                btnRemoveAll.classList.add("is-hidden");
            }
        }, 1);
    }

    private removeAllFilters(): void {
        const checkboxes = this.domNode.querySelectorAll(`input[type="checkbox"][${referenceAttr}]:checked`);
        [].forEach.call(checkboxes, this.uncheck);
        if (this.tagList !== null) {
            const tags = this.tagList.querySelectorAll("ux-tag[reference]");
            [].forEach.call(tags, (tag: HTMLElement) => tag.remove());
        }
        this.filterChange.emit();
        this.updateRemoveAll();
    }

    private handleTagListVisibility = () => {
        if (this.tagList === null) {
            return;
        }
        if (this.tagList.querySelector("ux-tag") === null) {
            this.tagList.classList.add("is-hidden");
        } else {
            this.tagList.classList.remove("is-hidden");
        }
    };

    private tagListObserver = new MutationObserver(this.handleTagListVisibility);

    componentDidLoad(): void {
        this.tagList = this.domNode.querySelector(".ux-tag-list");
        if (this.tagList !== null) {
            this.handleTagListVisibility();
            this.tagListObserver.observe(this.tagList, {
                childList: true
            });
        }
        this.initFilters();
        this.initSlot();
    }

    @Watch("endpoint")
    initFilters(): void {
        if (typeof this.endpoint !== "string" || this.endpoint === "") {
            // TODO: uninit/reset
            return;
        }

        fetch(this.endpoint)
            .then((r: Response) => r.json())
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            .then((response: any) => {
                this.filterValues = response.filterValues;

                this.domNode.querySelectorAll("ux-dropdown").forEach((item) => {
                    if (item.matches("[name]")) {
                        // @ts-expect-error Check for typing
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        new Array(response.filterValues[item.getAttribute("name")]).forEach((filterValuesInfo: Array<any>) => {
                            // @ts-expect-error Check for typing
                            item.querySelector("ux-list").setFilterValues(filterValuesInfo);
                        });
                    }
                });
            });
    }

    private initSlot(): void {
        /*
         * check if web components in slot are correctly loaded
         * this is a workaround because of this bug: https://github.com/ionic-team/stencil/issues/1130
         */
        if (this.domNode.querySelector(`ux-checkbox:not(.hydrated), ux-checkbox-list-item:not(.hydrated)`) !== null) {
            window.setTimeout(() => this.initSlot(), 50);
            return;
        }
        // init checkboxes that are checked at load
        const checkedCheckboxes = this.domNode.querySelectorAll(`input[type="checkbox"]:checked`);
        [].forEach.call(checkedCheckboxes, (checkbox: HTMLInputElement) => this.addTag(checkbox));
        // connect remove all filters button
        const btnRemoveAll = this.domNode.querySelector(removeAllSelector);
        if (btnRemoveAll !== null) {
            // @ts-expect-error Check for typing
            btnRemoveAll.addEventListener("click", (event: MouseEvent) => {
                event.preventDefault();
                this.removeAllFilters();
            });
            this.updateRemoveAll();
        }
    }

    render() {
        return (
            <Host class="ux-filter">
                <form onSubmit={this.onSubmit} onChange={this.onChange}>
                    <slot />
                </form>
            </Host>
        );
    }
}
